<template>
  <section id="manage-users">
    <page-header
      :breadcrumbs="breadcrumbs"
      :title="$dictionary.app.manageUsers.index.title"
    >
      <v-col
        cols="12"
        md="8"
        lg="6"
        class="d-flex align-center flex-wrap flex-lg-nowrap justify-end"
      >
        <v-spacer />
        <debounce-search-text-field
          text-field-class=""
          prop-to-search="email"
          placeholder="Search Users..."
          :lazy-search="getUsersList"
          :sort-by="sortConfig.sortBy"
          :sort-order="sortConfig.sortOrder"
          @search-value="userSearch = $event"
        />

        <div id="users-csv-btn" class="ml-4">
          <csv-button
            :labels="labels"
            :data="csvFileUsers"
            fileName="users"
            :fields="csvFields"
            :loading="isLoading"
            :color="$appConfig.csvButton.color.secondary"
          />
        </div>
      </v-col>
    </page-header>
    <v-row class="d-flex justify-center pt-3" no-gutters>
      <v-col cols="12" class="pt-3">
        <users-table
          :headers="headers"
          :is-loading="isLoading"
          :users="users"
          :fetch-users="fetchUsers"
          @get-records="getUsersList"
          @delete-user="onDeleteUser"
          @current-items="setCsvUsers"
        />
      </v-col>
    </v-row>

    <delete-user-dialog
      :dialog="deleteUserDialog.dialog"
      :sub-title="$dictionary.app.manageUsers.deleteUser.subTitle"
      :title="deleteUserDialog.title"
      @close="closeDeleteUserDialog"
      @delete="deleteUser"
      :is-loading="isUserDeleting"
    />
  </section>
</template>

<script>
import { mapActions } from "vuex";
import {
  PageHeader,
  DeleteRecordDialog as DeleteUserDialog,
  DebounceSearchTextField,
} from "@/components/shared";
import { CsvButton } from "@/components/widgets";

import { TitleMixin, SharedUtilsMixin } from "@/mixins";
import { deleteUser, getUsers } from "@/services";
import { UsersTable } from "@/components/tables";

import { usersHeaders as headers } from "@/data/table-headers";
import { RESPONSE_CODES, TABLE_PER_PAGES_OPTIONS } from "@/constants";
import {
  isEmpty,
  hasProp,
  csvFileLabels,
  isResponseCode,
} from "@/utils/common.utils";

/**
 * Manage users index page
 */
export default {
  name: "ManageUsers",
  /**
   *  View title
   */
  title: "Manage Users",
  /**
   * -------------- Mixins -------------
   */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
   * -------------- Components -------------
   */
  components: {
    PageHeader,
    CsvButton,
    UsersTable,
    DeleteUserDialog,
    DebounceSearchTextField,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    return {
      isLoading: false,
      isUserDeleting: false,
      users: [],
      csvFileUsers: [],
      userParams: {},
      headers,
      settings: {
        sort_by: "email",
        sort_order: "desc",
        itemsPerPage: 10,
        itemsPerPageOptions: TABLE_PER_PAGES_OPTIONS,
      },
      labels: csvFileLabels(headers),
      deleteUserDialog: {
        dialog: false,
        title: this.$dictionary.app.manageUsers.deleteUser.title,
        user: {},
      },
      isEditPermission: false,
      userDetails: {},
      fetchUsers: false,
      userSearch: "",
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    csvFields() {
      return this.labels && Object.keys(this.labels);
    },
    /**
     * Users listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("userParams", "email");
    },
    /**
     * Manage users breadcrumbs
     * @type {Array}
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.manageUsers.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
    /**
     * Id of the user whose details are being viewed
     * @type {String}
     */
    userId() {
      return this.deleteUserDialog.user?.id;
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Fetched the users list
     */
    async getUsersList(params = this.userParams) {
      try {
        if (this.userSearch && !hasProp(params, "q[email_cont]")) {
          params["q[email_cont]"] = this.userSearch;
        }
        this.userParams = params;
        this.isLoading = true;
        const { data } = await getUsers(params);
        this.users = data ? this.filterUsers(data) : [];
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.error,
          message: this.$errorMessage(error),
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Filter users
     * @param users Array of users
     * @returns {Array} filtered record of the users
     */
    filterUsers(users) {
      const { capitalize } = this.$options.filters;
      return users.map(({ first_name, last_name, ...user }) => ({
        ...user,
        first_name: capitalize(first_name),
        last_name: capitalize(last_name),
      }));
    },
    /**
     * Shows delete user notofication
     * @param {Object} item User details
     */
    onDeleteUser(user) {
      this.fetchUsers = false;
      const { title } = this.$dictionary.app.manageUsers.deleteUser;

      this.deleteUserDialog = {
        user,
        dialog: true,
        title: title.replace("<<name>>", user.email),
      };
    },
    /**
     * Closes delete user dialog
     */
    closeDeleteUserDialog() {
      this.deleteUserDialog = {
        dialog: false,
        title: "",
        user: {},
      };
    },
    /**
     * Delete user
     */
    async deleteUser() {
      try {
        if (isEmpty(this.deleteUserDialog.user)) return;

        this.isUserDeleting = true;
        const { status } = await deleteUser(this.userId);

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          const { successMsg } = this.$dictionary.app.manageUsers.deleteUser;
          this.setSnackbar({
            value: true,
            type: this.$appConfig.snackbar.snackbarTypes.success,
            message: successMsg.replace(
              "<<name>>",
              this.deleteUserDialog.user?.email
            ),
          });
          this.fetchUsers = true;
        }

        this.closeDeleteUserDialog();
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.error,
          message: this.$errorMessage(error),
        });
      } finally {
        this.isUserDeleting = false;
      }
    },
    /**
     * Sets csv users to bbe downloaded in csv file
     * @listens current-items
     */
    setCsvUsers(users) {
      this.csvFileUsers = users ? this.filterUsers(users) : [];
    },
  },
};
</script>
